import clsx from 'clsx';
import { MouseEvent, ReactNode, useCallback, useContext } from 'react';
import { CSSTransition } from 'react-transition-group';

import { GlobalsContext } from '../CommonAppContext';
import CloseButton, { CloseButtonSize } from './Close';
import style from './modal.module.css';

export enum ModalSizing {
  full,
  intrinsicHeight,
  fullDynamic,
}

export type ModalAlignment = 'center' | 'bottom';

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode | ReactNode[];
  header?: string;
  sizing?: ModalSizing;
  alignment?: ModalAlignment;
  showClose?: boolean;
  noAnimateAppear?: boolean;
}

export const Modal = ({ open, onClose, children, showClose, sizing, alignment, header, noAnimateAppear }: ModalProps) => {
  const { useMobileLayout } = useContext(GlobalsContext);

  const onCloseButtonClick = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const closeComponent = !showClose ? null : (
    <div className={clsx(style.closeBar)}>
      <CloseButton title="Close" size={useMobileLayout ? CloseButtonSize.medium : CloseButtonSize.small} onClick={onCloseButtonClick} />
    </div>
  );

  const onBackgroundClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (useMobileLayout && onClose !== undefined) {
        onClose();
      }
    },
    [useMobileLayout, onClose]
  );

  return (
    <CSSTransition in={open} timeout={300} classNames="display" unmountOnExit appear={!noAnimateAppear}>
      <div className={clsx(style.wrapper)}>
        <div className={clsx(style.background, 'background')} onClick={onBackgroundClick} aria-hidden="true" />
        <div
          className={clsx(
            style.root,
            {
              [style.intrinsicHeight]: sizing === ModalSizing.intrinsicHeight,
              bottomAlign: alignment === 'bottom',
              [style.bottomAlign]: alignment === 'bottom',
            },
            { [style.fullDynamic]: sizing === ModalSizing.fullDynamic }
          )}
        >
          <div
            className={clsx(
              style.container,
              { [style.intrinsicHeight]: sizing === ModalSizing.intrinsicHeight },
              { [style.fullDynamic]: sizing === ModalSizing.fullDynamic }
            )}
          >
            {closeComponent}
            <div className={clsx(style.content)}>
              {header ? <h2 className={clsx(style.header)}>{header}</h2> : null}
              <div className={clsx(style.innerContent)}>{children}</div>
            </div>
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};
